"use client";

import {
  trackListingQuoteFormView,
  trackListingQuoteSubmitSuccess,
} from "@/lib/analytics/gtm-lead-metadata";
import { useCallback, useEffect, useRef } from "react";
import { track } from "@/lib/analytics/tracker";
import { classifyLeadQuality } from "@convex/leads/helpers/classifyLeadQuality.lib";
import { captureError } from "@/lib/error-tracking";
import type { Id } from "@convex/_generated/dataModel";
import type { ProductDetail } from "./types";
import type { QuoteFormState } from "./listing-quote-request-state";
import { useListingActionSettled } from "./listing-action-resolution";

const FORM_ID = "listing-quote-request-card";

export function useTrackQuoteFormView(data: ProductDetail) {
  const isSettled = useListingActionSettled();
  useEffect(() => {
    if (!isSettled) return;
    trackListingQuoteFormView({
      formId: FORM_ID,
      listingId: data._id,
      productTypeSlug: data.productTypeSlug || "pallets",
      productTypeName: data.productType?.displayName || data.title,
    });
  }, [isSettled, data._id, data.productType?.displayName, data.productTypeSlug, data.title]);
}

const PATCH_START_FIELDS = [
  "quantityDisplay",
  "fulfillmentType",
  "preferredDate",
  "contact",
  "notes",
] as const satisfies readonly (keyof QuoteFormState)[];

export type QuoteStartField =
  | (typeof PATCH_START_FIELDS)[number]
  | "deliveryAddress";

export function useTrackQuoteFormStart(listingId: Id<"products">) {
  const started = useRef(false);
  const markStarted = useCallback(
    (firstField: QuoteStartField) => {
      if (started.current) return;
      started.current = true;
      track("lead.form_started", {
        source: "listing_quote",
        formId: FORM_ID,
        listingId,
        firstField,
      });
    },
    [listingId],
  );
  const markPatchStarted = useCallback(
    (patch: Partial<QuoteFormState>) => {
      const field = PATCH_START_FIELDS.find((key) => key in patch);
      if (field) markStarted(field);
    },
    [markStarted],
  );
  return { markStarted, markPatchStarted };
}

export function trackQuoteRequestBlocked(
  listingId: Id<"products">,
  blockedByField: string,
) {
  track("lead.form_blocked", {
    source: "listing_quote",
    formId: FORM_ID,
    listingId,
    blockedByField,
  });
}

export function trackQuoteRequestSubmitted({
  listingId,
  productTypeSlug,
  productTypeName,
  quantity,
  city,
  state,
  leadId,
  leadNumber,
}: {
  listingId: string;
  productTypeSlug: string;
  productTypeName: string;
  quantity: number;
  city?: string;
  state?: string;
  leadId: string;
  leadNumber: string;
}) {
  try {
    const quality = classifyLeadQuality({
      productTypeSlug,
      quantity,
      leadType: "buy",
    });
    trackListingQuoteSubmitSuccess({
      formId: FORM_ID,
      listingId,
      leadNumber,
      leadId,
      city,
      state,
      productTypeSlug,
      productTypeName,
      quantity,
      leadQuality: quality.tier,
      estimatedValue: quality.estimatedValueCents / 100,
    });
  } catch (trackingError) {
    captureError(trackingError, {
      tags: { where: "listing-quote-request-form.trackListingQuoteSubmit" },
      extra: { listingId, leadNumber, leadId },
    });
  }
}
